/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import breakpoints from '@/styles/modules/breakpoints.module.scss';
import FlightSelectorSlide from '../flight-selector-slide/flight-selector-slide';
import styles from './flight-selector-slider.module.scss';

interface FlightSelectorSliderProps {
  /** The active item */
  activeItem?: Content.Components['FlightSelectorTransportDto'] | null;
  /** The list of items */
  items: Content.Components['FlightSelectorTransportDto'][];
  /** The tooltip content to show when there is no time */
  noTimeHeadline: string;
  /** The text to show when there is no time */
  noTimeText: string;
  /** An event that is triggered on click */
  onClick: (value: Content.Components['FlightSelectorTransportDto']) => void;
}

const FlightSelectorSlider = ({
  activeItem,
  items = [],
  noTimeHeadline,
  noTimeText,
  onClick,
}: FlightSelectorSliderProps) => {
  const swiperRef = useRef<SwiperInstance | null>(null);
  const [activeIndex, setActiveIndex] = useState(() => {
    const activeItemIndex = items.findIndex(item => item.id === activeItem?.id);

    return activeItemIndex > -1 ? activeItemIndex : 0;
  });

  const handlePrevious = () => {
    if (!swiperRef.current) return;

    swiperRef.current.slidePrev();
  };

  const handleNext = () => {
    if (!swiperRef.current) return;

    swiperRef.current.slideNext();
  };

  return (
    <div className={styles.component}>
      <Swiper
        allowSlideNext={activeIndex < items.length - 1}
        breakpoints={{
          [parseInt(breakpoints.mobile)]: {
            slidesPerView: 1.1,
            spaceBetween: 16,
          },

          [parseInt(breakpoints.tablet)]: {
            slidesPerView: 1.3,
            spaceBetween: 32,
          },
        }}
        className={styles.swiper}
        initialSlide={activeIndex}
        onActiveIndexChange={({ activeIndex }) => setActiveIndex(activeIndex)}
        onSwiper={swiper => (swiperRef.current = swiper)}
        wrapperClass={styles.swiperWrapper}
      >
        {items.map((flight, index) => {
          const isActive = activeItem?.id === flight.id;
          const isFocused = activeIndex === index;
          const isSelectedFlight = activeItem?.id === flight.id;

          const isPriceRelative = !!activeItem && !isSelectedFlight;
          let flightPrice = flight.totalPrice;

          if (activeItem && !isSelectedFlight) {
            flightPrice = flight.totalPrice - activeItem.totalPrice;
          }

          return (
            <SwiperSlide
              key={flight.id}
              className={classNames(styles.slide, {
                [styles.isBefore]: activeIndex > index,
                [styles.isActive]: isFocused,
              })}
            >
              <FlightSelectorSlide
                {...flight}
                className={styles.item}
                isActive={isActive}
                isPriceRelative={isPriceRelative}
                noTimeHeadline={noTimeHeadline}
                noTimeText={noTimeText}
                onClick={() => onClick(flight)}
                totalPrice={flightPrice}
              />
            </SwiperSlide>
          );
        })}

        {/* Empty slide is required to align last slide to the left */}
        <SwiperSlide />
      </Swiper>

      {items.length > 1 && (
        <div className={styles.navigation}>
          <Button
            icon="ChevronLeft"
            onClick={handlePrevious}
            variant="tertiary"
          />

          <Typography tagStyle="labelMedium">{`${activeIndex + 1} / ${items.length}`}</Typography>

          <Button icon="ChevronRight" onClick={handleNext} variant="tertiary" />
        </div>
      )}
    </div>
  );
};

FlightSelectorSlider.displayName = 'FlightSelectorSlider';

export default FlightSelectorSlider;
